import type { SuggestionItem } from "@/features/timesheetFlow/components/SuggestionPickerDialog";
import {
  getKundeStandardMaterials,
  getOrdnerStandardMaterials,
  getSOrdnerStandardMaterials,
  type OrderedMaterialSuggestion,
} from "@/features/timesheetFlow/timesheetFlowApi";
import type { TimesheetMaterialRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import { isNwPlusFpAuftragsart } from "@/features/timesheetFlow/lib/auftragsart";
import { resolveMaterialSourceTypeForApi } from "@/features/timesheetFlow/lib/materialSourceType";
import {
  dokumentArtLabel,
  formatAngebotMaterialBadge,
} from "@/features/timesheetFlow/lib/timesheetMaterialSource";

function formatOrderedMaterialSourceLabel(
  sourceType: string | null | undefined,
  quelle?: string | null,
): string {
  if (sourceType === "order") return "Bestellung";
  if (sourceType === "lager" || sourceType === "ma_position") return "Lager / bereitgelegt";
  if (sourceType === "festpreis" || sourceType === "gd_position" || sourceType === "angebot") {
    return `Festpreis ${dokumentArtLabel(quelle)}`;
  }
  if (sourceType === "manual") return "manueller Eintrag";
  return sourceType || "unbekannte Quelle";
}

/** Stabile Vorschlags-ID (auch nach Reload ohne suggestion_value). */
export function materialSuggestionValue(mat: {
  suggestion_value?: string | null;
  order_line_id?: number | null;
  bestellung_position_id?: number | null;
  ma_position_id?: number | null;
  gd_position_id?: number | null;
}): string | null {
  if (mat.suggestion_value) return String(mat.suggestion_value);
  if (mat.gd_position_id != null) return `gd:${mat.gd_position_id}`;
  if (mat.ma_position_id != null) return `ma:${mat.ma_position_id}`;
  if (mat.order_line_id != null) return String(mat.order_line_id);
  if (mat.bestellung_position_id != null) return `bp:${mat.bestellung_position_id}`;
  return null;
}

/** Bestellposition (nicht Lager/bereitgelegt und nicht Festpreis aus Angebot). */
export function isOrderedArticleSuggestion(mat: {
  source_type?: string | null;
  source_type_db?: string | null;
  order_line_id?: number | null;
  ma_position_id?: number | null;
  gd_position_id?: number | null;
}): boolean {
  const source = String(mat.source_type || "").trim().toLowerCase();
  const db = String(mat.source_type_db || "").trim().toLowerCase();
  if (source === "lager" || source === "ma_position" || db === "ma_position" || mat.ma_position_id != null) {
    return false;
  }
  if (
    source === "festpreis" ||
    source === "gd_position" ||
    source === "angebot" ||
    db === "gd_position" ||
    mat.gd_position_id != null
  ) {
    return false;
  }
  return source === "order" || db === "order_line" || mat.order_line_id != null;
}

/**
 * Bei NW+FP gehören bestellte Artikel nicht in den Stundennachweis.
 * Im Lager bereitgelegte Positionen bleiben als Vorschläge erhalten.
 */
export function filterOrderedArticlesForAuftragsart<T extends Parameters<typeof isOrderedArticleSuggestion>[0]>(
  items: T[],
  auftragsart: unknown,
): T[] {
  if (!isNwPlusFpAuftragsart(auftragsart)) return items;
  return items.filter((item) => !isOrderedArticleSuggestion(item));
}

/** Vorschlagsliste für bestellte / bereitgelegte Materialpositionen. */
export function buildOrderedMaterialSuggestionItems(
  items: OrderedMaterialSuggestion[],
  currentTimesheetId: number | null,
): SuggestionItem[] {
  return items.map((mat) => {
    const value = String(mat.value || "");
    const title = String(mat.text || mat.label || "(ohne Bezeichnung)");
    const qty = mat.default_menge ?? mat.menge;
    const unit = mat.einheit || "";
    const metaParts = [
      formatOrderedMaterialSourceLabel(mat.source_type, mat.quelle_typ || mat.quelle_label),
    ];
    if (qty != null && String(qty).trim() !== "") {
      metaParts.push(unit ? `${qty} ${unit}` : String(qty));
    }
    if (mat.bestellnummer) {
      const isLager = mat.source_type === "lager" || mat.source_type_db === "ma_position";
      const isFestpreis =
        mat.source_type === "festpreis" ||
        mat.source_type_db === "gd_position" ||
        mat.gd_position_id != null;
      if (isFestpreis) {
        metaParts.push(String(mat.bestellnummer));
      } else {
        metaParts.push(isLager ? `MA ${mat.bestellnummer}` : `Bestellnr. ${mat.bestellnummer}`);
      }
    }

    const usedTimesheetId = mat.used_timesheet_id ?? null;
    const alreadyUsedElsewhere =
      usedTimesheetId != null && usedTimesheetId !== currentTimesheetId;
    const alreadyUsedHere =
      usedTimesheetId != null && usedTimesheetId === currentTimesheetId;

    return {
      id: value,
      label: title,
      imageSrc: mat.image_src || null,
      meta: metaParts.filter(Boolean).join(" · "),
      disabled: alreadyUsedElsewhere || alreadyUsedHere,
      disabledReason: alreadyUsedElsewhere
        ? "Bereits in Stundennachweis"
        : alreadyUsedHere
          ? "Bereits übernommen"
          : undefined,
    };
  });
}

export function suggestionToMaterialRow(
  item: OrderedMaterialSuggestion,
  currentTimesheetId: number | null,
): TimesheetMaterialRecord | null {
  if (item.used_timesheet_id && item.used_timesheet_id !== currentTimesheetId) {
    return null;
  }

  const mengeRaw = item.default_menge ?? item.menge;
  const menge = Number(mengeRaw);
  if (!Number.isFinite(menge) || menge <= 0) return null;

  const artikelId = item.artikel_id ?? null;
  const sourceType = resolveMaterialSourceTypeForApi(item.source_type_db || item.source_type, {
    artikel_id: artikelId,
  });
  const isFestpreis =
    sourceType === "gd_position" ||
    item.source_type === "festpreis" ||
    item.source_type_db === "gd_position" ||
    item.gd_position_id != null;
  const quelle = String(item.quelle_typ || item.quelle_label || "").trim() || null;
  const angebotBadge = isFestpreis
    ? formatAngebotMaterialBadge(item.bestellnummer, quelle)
    : null;

  return {
    menge,
    einheit: item.einheit || null,
    text: item.text || item.label || "",
    bemerkung: angebotBadge || item.bemerkung || null,
    source_type: sourceType,
    order_line_id: item.order_line_id ?? null,
    bestellung_position_id: item.bestellung_position_id ?? null,
    ma_position_id: item.ma_position_id ?? null,
    gd_position_id: item.gd_position_id ?? null,
    artikel_id: artikelId,
    hero_nr: item.hero_nr ?? item.artikelnummer ?? null,
    image_src: item.image_src ?? null,
    suggestion_value: item.value,
    source_document_nummer: isFestpreis ? item.bestellnummer || null : null,
    quelle_typ: isFestpreis ? quelle : null,
  };
}

export function importMaterialSuggestions(
  items: OrderedMaterialSuggestion[],
  selectedValues: Set<string>,
  currentTimesheetId: number | null,
  existing: TimesheetMaterialRecord[],
): TimesheetMaterialRecord[] {
  const existingValues = new Set(
    existing
      .map((m) => materialSuggestionValue(m))
      .filter((value): value is string => Boolean(value)),
  );
  const added: TimesheetMaterialRecord[] = [];

  for (const item of items) {
    const value = item.value;
    if (!value || !selectedValues.has(value)) continue;
    if (existingValues.has(value)) continue;
    const row = suggestionToMaterialRow(item, currentTimesheetId);
    if (row) {
      added.push(row);
      existingValues.add(value);
    }
  }

  return added;
}

export function standardMaterialToRow(
  mat: OrderedMaterialSuggestion,
): TimesheetMaterialRecord | null {
  const label = String(mat.text || mat.label || "").trim();
  if (!label) return null;

  const mengeRaw = mat.menge ?? mat.default_menge;
  const menge = Number(mengeRaw);
  const heroNr = String(mat.hero_nr || mat.artikelnummer || "").trim() || null;
  const artikelId = mat.artikel_id ?? null;

  return {
    menge: Number.isFinite(menge) && menge > 0 ? menge : 1,
    einheit: mat.einheit || null,
    text: label,
    source_type: artikelId ? "hero" : "manual",
    artikel_id: artikelId,
    hero_nr: heroNr,
  };
}

export async function loadStandardMaterialsForProject(project: {
  kunden_id?: number | null;
  ordner_id?: number | null;
  sordner_id?: number | null;
}): Promise<{
  materials: TimesheetMaterialRecord[];
  source: "sordner" | "ordner" | "kunde" | null;
}> {
  if (project.sordner_id) {
    const sordnerStd = await getSOrdnerStandardMaterials(project.sordner_id);
    if (sordnerStd.length) {
      const materials = sordnerStd
        .map((m) => standardMaterialToRow(m))
        .filter(Boolean) as TimesheetMaterialRecord[];
      return { materials, source: "sordner" };
    }
  } else if (project.ordner_id) {
    const ordnerStd = await getOrdnerStandardMaterials(project.ordner_id);
    if (ordnerStd.length) {
      const materials = ordnerStd
        .map((m) => standardMaterialToRow(m))
        .filter(Boolean) as TimesheetMaterialRecord[];
      return { materials, source: "ordner" };
    }
  }

  if (project.kunden_id) {
    const kundeStd = await getKundeStandardMaterials(project.kunden_id);
    const materials = kundeStd
      .map((m) => standardMaterialToRow(m))
      .filter(Boolean) as TimesheetMaterialRecord[];
    return { materials, source: materials.length ? "kunde" : null };
  }

  return { materials: [], source: null };
}

export function validateMaterialsForReady(materials: TimesheetMaterialRecord[]): string | null {
  for (const m of materials) {
    const text = String(m.text || "").trim();
    const menge = Number(m.menge);
    if (text && (!Number.isFinite(menge) || menge <= 0)) {
      return "Materialzeilen benötigen Text und Menge > 0 für „Fertigstellen“.";
    }
    if (Number.isFinite(menge) && menge > 0 && !text) {
      return "Materialzeilen benötigen Text und Menge > 0 für „Fertigstellen“.";
    }
  }
  return null;
}
